import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Button,
  Switch,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from "@mui/icons-material";
import clientsService from "../services/api/clientsService";
import appRegistryService from "../services/api/appRegistryService";

/**
 * ClientAppMatrixPage
 *
 * Shows the per-client app matrix: for a selected client, lists every
 * IntegrationApp in the catalog and whether it's enabled for that client
 * (via ClientAppConfig).
 *
 * Also exposes the "Sync Catalog" button which enqueues a BullMQ sync job
 * that reads the client's Applications table (Metabase API → direct DB →
 * mock fallback) and upserts AppDefinition + ClientAppConfig rows.
 */
const ClientAppMatrixPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientTag, setSelectedClientTag] = useState("");
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [enabledFilter, setEnabledFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Seconds remaining on the client-side cooldown window (mirrors the
  // backend's 15s Redis-backed debounce).
  const [refreshCooldown, setRefreshCooldown] = useState(0);

  // Load clients once
  useEffect(() => {
    (async () => {
      try {
        const res = await clientsService.getClients();
        setClients(res.data);
        if (res.data.length > 0) {
          setSelectedClientTag(res.data[0].tag);
        }
      } catch (e) {
        setError(`Failed to load clients: ${e.message}`);
      } finally {
        setLoadingClients(false);
      }
    })();
  }, []);

  const loadMatrix = async (tag) => {
    if (!tag) return;
    setLoading(true);
    setError(null);
    try {
      const res = await appRegistryService.getMatrixByClientTag(tag);
      setMatrix(res.data);
    } catch (e) {
      setError(`Failed to load matrix: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix(selectedClientTag);
  }, [selectedClientTag]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.tag === selectedClientTag),
    [clients, selectedClientTag],
  );

  // Countdown tick for the Refresh-all cooldown.
  useEffect(() => {
    if (refreshCooldown <= 0) return undefined;
    const t = setTimeout(() => setRefreshCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [refreshCooldown]);

  const triggerRefreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await appRegistryService.refreshNow();
      if (res.status === 202) {
        setSnackbar(
          `Refreshing… (background job ${res.data.jobId} started, poll for status)`,
        );
        setRefreshCooldown(res.data.debounceWindow || 15);
      } else if (res.status === 429) {
        const secs = Math.ceil((res.data.retryAfterMs || 15000) / 1000);
        setSnackbar(`Refresh debounced — try again in ${secs}s`);
        setRefreshCooldown(secs);
      }
    } catch (e) {
      setSnackbar(`Refresh failed: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const triggerSync = async () => {
    if (!selectedClient) return;
    setSyncing(true);
    try {
      const res = await appRegistryService.triggerSyncForClient(selectedClient.id);
      setSnackbar(`Sync job ${res.data.jobId} enqueued for ${selectedClient.tag}.`);
      // Poll job for a short while then refresh
      setTimeout(() => loadMatrix(selectedClientTag), 15000);
    } catch (e) {
      setSnackbar(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const kinds = useMemo(() => {
    const set = new Set();
    matrix.forEach((row) => row.app.kind && set.add(row.app.kind));
    return ["all", ...Array.from(set).sort()];
  }, [matrix]);

  const filtered = useMemo(() => {
    return matrix.filter((row) => {
      const app = row.app;
      if (search) {
        const s = search.toLowerCase();
        const hay = `${app.name} ${app.configKey} ${app.shortName || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (kindFilter !== "all" && app.kind !== kindFilter) return false;
      if (enabledFilter === "enabled" && !(row.config && row.config.isEnabled)) return false;
      if (enabledFilter === "disabled" && row.config && row.config.isEnabled) return false;
      if (enabledFilter === "unconfigured" && row.config !== null) return false;
      return true;
    });
  }, [matrix, search, kindFilter, enabledFilter]);

  const enabledCount = useMemo(
    () => matrix.filter((r) => r.config && r.config.isEnabled).length,
    [matrix],
  );
  const configuredCount = useMemo(() => matrix.filter((r) => r.config).length, [matrix]);

  const toggleEnabled = async (row) => {
    const app = row.app;
    const nextEnabled = !(row.config && row.config.isEnabled);
    try {
      await appRegistryService.upsertClientConfig(app.id, selectedClientTag, {
        isEnabled: nextEnabled,
      });
      // Optimistic update
      setMatrix((prev) =>
        prev.map((r) =>
          r.app.id === app.id
            ? { ...r, config: { ...(r.config || {}), isEnabled: nextEnabled } }
            : r,
        ),
      );
    } catch (e) {
      setSnackbar(`Failed to update: ${e.message}`);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Client App Matrix
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage which apps are enabled at each client. Toggle individual apps, or
        run a catalog sync to refresh enablement from the client's Applications
        table.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={selectedClientTag}
              onChange={(e) => setSelectedClientTag(e.target.value)}
              disabled={loadingClients}
            >
              {clients.map((c) => (
                <MenuItem key={c.tag} value={c.tag}>
                  {c.name} ({c.tag}) — {c.environment}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search apps..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
            >
              {kinds.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={enabledFilter}
              onChange={(e) => setEnabledFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="unconfigured">Unconfigured</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Reload matrix">
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => loadMatrix(selectedClientTag)}
              disabled={loading}
            >
              Reload
            </Button>
          </Tooltip>

          <Tooltip title="Enqueue a full catalog refresh across all clients (15s global debounce)">
            <span>
              <Button
                data-testid="refresh-all-now-btn"
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={triggerRefreshAll}
                disabled={refreshing || refreshCooldown > 0}
              >
                {refreshCooldown > 0
                  ? `Wait ${refreshCooldown}s`
                  : refreshing
                  ? "Refreshing…"
                  : "Refresh all (sync now)"}
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Trigger App Catalog Sync for this client">
            <span>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={triggerSync}
                disabled={syncing || !selectedClient}
              >
                {syncing ? "Syncing..." : "Sync Catalog"}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Chip label={`Total: ${matrix.length}`} />
          <Chip label={`Configured: ${configuredCount}`} color="info" variant="outlined" />
          <Chip label={`Enabled: ${enabledCount}`} color="success" />
          {selectedClient?.lastSyncAt && (
            <Chip
              label={`Last sync: ${new Date(selectedClient.lastSyncAt).toLocaleString()}`}
              variant="outlined"
            />
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Enabled</TableCell>
                <TableCell>App</TableCell>
                <TableCell>configKey</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>SSO</TableCell>
                <TableCell>Scripts</TableCell>
                <TableCell>Last Config Sync</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => {
                const enabled = !!(row.config && row.config.isEnabled);
                const hasConfig = !!row.config;
                return (
                  <TableRow key={row.app.id} hover>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={enabled}
                        onChange={() => toggleEnabled(row)}
                      />
                      {hasConfig ? (
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{ verticalAlign: "middle", ml: 1, color: "text.disabled" }}
                        />
                      ) : (
                        <Tooltip title="No ClientAppConfig row yet">
                          <UncheckedIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", ml: 1, color: "text.disabled" }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {row.app.logo && (
                          <img
                            src={row.app.logo}
                            alt=""
                            style={{ width: 24, height: 24, borderRadius: 4 }}
                          />
                        )}
                        <Typography variant="body2">{row.app.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <code>{row.app.configKey}</code>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.app.kind || "other"} size="small" />
                    </TableCell>
                    <TableCell>{row.app.ssoType}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {row.app.hasLoginScript && <Chip size="small" label="login" />}
                        {row.app.hasRosterSync && <Chip size="small" label="roster" />}
                        {row.app.hasProgressCollection && (
                          <Chip size="small" label="progress" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.config?.lastSyncAt
                        ? new Date(row.config.lastSyncAt).toLocaleString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No apps match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        message={snackbar || ""}
      />
    </Container>
  );
};

export default ClientAppMatrixPage;
